import { useState, useEffect } from "react";
import { Phone, Mail, MapPin, Send, CheckCircle, Shield, Award, Zap, ThumbsUp, ChevronDown, ChevronUp, Star, ExternalLink, ClipboardCheck, Home, Wrench, ArrowRight, Users, Clock } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Card, CardContent } from "../components/ui/card";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const GOOGLE_MAPS_URL = "https://maps.app.goo.gl/C8s7vSJPZ2sHXxe16";

const COMMUNES = [
  { name: "Villeneuve-sur-Lot", url: "/installation-pompe-a-chaleur-villeneuve-sur-lot", distance: "—", pop: "~22 000 hab" },
  { name: "Tonneins", url: "/installation-pompe-a-chaleur-tonneins", distance: "25 min", pop: "~9 300 hab" },
  { name: "Aiguillon", url: "/installation-pompe-a-chaleur-aiguillon", distance: "30 min", pop: "~4 400 hab" },
  { name: "Clairac", url: "/installation-pompe-a-chaleur-clairac", distance: "30 min", pop: "~2 500 hab" },
  { name: "Fumel", url: "/installation-pompe-a-chaleur-fumel", distance: "20 min", pop: "~5 000 hab" },
  { name: "Penne-d'Agenais", url: "/installation-pompe-a-chaleur-penne-d-agenais", distance: "15 min", pop: "~2 300 hab" },
  { name: "Sainte-Livrade-sur-Lot", url: "/installation-pompe-a-chaleur-sainte-livrade-sur-lot", distance: "10 min", pop: "~6 200 hab" },
  { name: "Monflanquin", url: "/installation-pompe-a-chaleur-monflanquin", distance: "25 min", pop: "~2 300 hab" },
  { name: "Miramont-de-Guyenne", url: "/installation-pompe-a-chaleur-miramont-de-guyenne", distance: "30 min", pop: "~3 200 hab" },
  { name: "Casseneuil", url: "/installation-pompe-a-chaleur-casseneuil", distance: "8 min", pop: "~2 400 hab" },
  { name: "Bias", url: "/installation-pompe-a-chaleur-bias", distance: "5 min", pop: "~3 700 hab" },
  { name: "Pujols", url: "/installation-pompe-a-chaleur-pujols", distance: "10 min", pop: "~3 600 hab" },
  { name: "Cancon", url: "/installation-pompe-a-chaleur-cancon", distance: "20 min", pop: "~1 300 hab" },
  { name: "Monsempron-Libos", url: "/installation-pompe-a-chaleur-monsempron-libos", distance: "20 min", pop: "~2 000 hab" },
  { name: "Castillonnès", url: "/installation-pompe-a-chaleur-castillonnes", distance: "25 min", pop: "~1 400 hab" },
  { name: "Villeréal", url: "/installation-pompe-a-chaleur-villereal", distance: "30 min", pop: "~1 300 hab" },
  { name: "Damazan", url: "/installation-pompe-a-chaleur-damazan", distance: "25 min", pop: "~1 500 hab" },
  { name: "Port-Sainte-Marie", url: "/installation-pompe-a-chaleur-port-sainte-marie", distance: "25 min", pop: "~2 000 hab" },
];

const FAQS = [
  {
    question: "François Delattre intervient-il dans tout le Lot-et-Garonne ?",
    answer: "Oui, François couvre l'ensemble du Lot-et-Garonne depuis sa base de Pinel-Hauterive. Il intervient régulièrement dans 18 communes du département, de Villeneuve-sur-Lot à Tonneins, en passant par Aiguillon, Fumel, Monflanquin, Miramont-de-Guyenne et bien d'autres. Le déplacement et le diagnostic sont gratuits, quelle que soit votre localisation dans le 47."
  },
  {
    question: "Combien coûte une pompe à chaleur en Lot-et-Garonne ?",
    answer: "Dans le Lot-et-Garonne, l'installation d'une pompe à chaleur représente un investissement de 8 000€ à 15 000€ avant aides. Grâce à MaPrimeRénov' (jusqu'à 5 000€), aux primes CEE (jusqu'à 6 550€ depuis mars 2025) et à la TVA réduite à 5,5%, le reste à charge peut descendre à environ 950€ pour une maison de 100m². François établit un devis gratuit et personnalisé après visite."
  },
  {
    question: "Pourquoi choisir un installateur RGE QualiPAC dans le 47 ?",
    answer: "La certification RGE QualiPAC est la condition obligatoire pour accéder aux aides de l'État (MaPrimeRénov', CEE). Elle garantit que l'installateur maîtrise la pose de pompes à chaleur selon les normes en vigueur. François Delattre détient cette certification depuis de nombreuses années et cumule plus de 500 installations en Lot-et-Garonne."
  },
  {
    question: "PAC air/eau ou air/air : laquelle choisir dans le 47 ?",
    answer: "Le choix dépend de votre logement. La PAC air/eau est idéale si vous avez des radiateurs ou un plancher chauffant : elle remplace votre chaudière fioul ou gaz sans modifier le réseau et produit aussi l'eau chaude sanitaire. La PAC air/air est parfaite pour les habitations sans circuit hydraulique : elle assure chauffage et climatisation, un vrai atout dans le Lot-et-Garonne où les étés sont chauds. François vous conseille gratuitement après visite."
  },
  {
    question: "Quel délai pour installer une pompe à chaleur dans le Lot-et-Garonne ?",
    answer: "La pose prend généralement 1 à 2 jours, où que vous soyez dans le département. François gère aussi l'intégralité de vos dossiers MaPrimeRénov' et CEE sans surcoût, ce qui vous évite des semaines de paperasse."
  },
  {
    question: "Le diagnostic et le devis sont-ils vraiment gratuits ?",
    answer: "Oui, sans aucune condition. François se déplace gratuitement partout dans le Lot-et-Garonne pour examiner votre installation, dimensionner la PAC adaptée et vous remettre un devis détaillé avec toutes les aides déduites. Aucun engagement, aucun frais caché."
  }
];

function GoogleLogo() {
  return (
    <svg viewBox="0 0 24 24" className="w-6 h-6" xmlns="http://www.w3.org/2000/svg">
      <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/>
      <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
      <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
      <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
    </svg>
  );
}

function StarRating({ rating }) {
  return (
    <div className="flex gap-0.5">
      {Array.from({ length: 5 }).map((_, i) => (
        <Star key={i} className={`w-4 h-4 ${i < rating ? "text-yellow-400 fill-yellow-400" : "text-gray-200 fill-gray-200"}`} />
      ))}
    </div>
  );
}

function getInitials(name) {
  return name.split(" ").map(w => w[0]).join("").toUpperCase().slice(0, 2);
}

function ReviewCard({ review }) {
  return (
    <Card className="bg-white border border-gray-100 rounded-xl shadow-sm h-full">
      <CardContent className="p-5 md:p-6">
        <div className="flex items-center gap-3 mb-3">
          {review.photo ? (
            <img src={review.photo} alt={review.name} className="w-9 h-9 rounded-full object-cover" width={36} height={36} />
          ) : (
            <div className="w-9 h-9 bg-[#6AB023] rounded-full flex items-center justify-center text-white font-bold text-xs">{getInitials(review.name)}</div>
          )}
          <div className="flex-1 min-w-0">
            <p className="font-semibold text-gray-900 text-sm">{review.name}</p>
            <p className="text-xs text-gray-400">{review.date}</p>
          </div>
          <GoogleLogo />
        </div>
        <StarRating rating={review.rating} />
        <p className="text-sm text-gray-700 leading-relaxed mt-3">{review.text}</p>
      </CardContent>
    </Card>
  );
}

function FAQItem({ faq, defaultOpen = false }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="border border-gray-200 rounded-xl overflow-hidden">
      <button onClick={() => setOpen(!open)} className="w-full flex items-center justify-between p-5 text-left bg-white hover:bg-gray-50 transition-colors">
        <span className="font-semibold text-gray-900 text-sm md:text-base">{faq.question}</span>
        {open ? <ChevronUp className="w-5 h-5 text-[#6AB023] flex-shrink-0" /> : <ChevronDown className="w-5 h-5 text-[#6AB023] flex-shrink-0" />}
      </button>
      {open && <div className="px-5 pb-5 bg-white"><p className="text-sm text-gray-600 leading-relaxed">{faq.answer}</p></div>}
    </div>
  );
}

function MiniReviewBadge({ rating, total }) {
  return (
    <div className="flex items-center gap-2 text-sm text-gray-600">
      <GoogleLogo />
      <div className="flex gap-0.5">
        {Array.from({ length: 5 }).map((_, i) => (
          <Star key={i} className={`w-3.5 h-3.5 ${i < Math.round(rating) ? "text-yellow-400 fill-yellow-400" : "text-gray-200 fill-gray-200"}`} />
        ))}
      </div>
      <span className="font-semibold text-gray-900">{rating.toFixed(1)}</span>
      <span className="text-gray-400">({total} avis)</span>
    </div>
  );
}

function ContactForm({ rating, total }) {
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [service, setService] = useState("PAC Air/Eau");
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!name.trim() || !phone.trim()) { setError("Veuillez remplir votre nom et téléphone."); return; }
    setLoading(true);
    try {
      await axios.post(`${API}/leads`, { name: name.trim(), phone: phone.trim(), service, message: message.trim() });
      setSuccess(true);
      setName(""); setPhone(""); setService("PAC Air/Eau"); setMessage("");
    } catch { setError("Une erreur est survenue. Veuillez nous appeler directement au 06 28 51 49 15."); }
    finally { setLoading(false); }
  };

  if (success) return (
    <div className="flex flex-col items-center text-center py-8">
      <div className="w-16 h-16 bg-green-50 rounded-full flex items-center justify-center mb-4"><CheckCircle className="w-8 h-8 text-[#6AB023]" /></div>
      <h3 className="text-xl font-bold text-gray-900 mb-2">Demande envoyée !</h3>
      <p className="text-gray-600 text-sm">François vous rappelle sous 24h pour organiser sa venue chez vous.</p>
      <Button onClick={() => setSuccess(false)} variant="outline" className="mt-6 border-[#6AB023] text-[#6AB023] hover:bg-green-50 rounded-full">Nouvelle demande</Button>
    </div>
  );

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <h3 className="text-xl font-bold text-gray-900 mb-1">Devis gratuit — sans engagement</h3>
        <p className="text-sm text-gray-500">François vous rappelle sous 24h.</p>
      </div>

      {total > 0 && (
        <div className="review-badge-pulse bg-green-50/60 rounded-lg p-3 flex items-center justify-center">
          <MiniReviewBadge rating={rating} total={total} />
        </div>
      )}

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1.5">Votre nom <span className="text-red-400">*</span></label>
        <Input type="text" placeholder="Jean Dupont" value={name} onChange={(e) => setName(e.target.value)} className="h-12 border-gray-300 contact-input-focus rounded-lg" />
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1.5">Votre téléphone <span className="text-red-400">*</span></label>
        <Input type="tel" placeholder="06 12 34 56 78" value={phone} onChange={(e) => setPhone(e.target.value)} className="h-12 border-gray-300 contact-input-focus rounded-lg" />
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1.5">Type de projet</label>
        <select value={service} onChange={(e) => setService(e.target.value)} className="w-full h-12 border border-gray-300 rounded-lg px-3 text-sm text-gray-700 bg-white contact-input-focus focus:outline-none">
          <option value="PAC Air/Eau">Installation PAC Air/Eau</option>
          <option value="PAC Air/Air">Installation PAC Air/Air</option>
          <option value="Chauffe-eau thermodynamique">Chauffe-eau thermodynamique</option>
          <option value="Climatisation réversible">Climatisation réversible</option>
          <option value="Remplacement chaudière fioul">Remplacement chaudière fioul</option>
          <option value="Autre">Autre</option>
        </select>
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1.5">Votre commune <span className="text-xs text-gray-400 font-normal">(optionnel)</span></label>
        <Input type="text" placeholder="Ex: Villeneuve-sur-Lot, Tonneins..." value={message} onChange={(e) => setMessage(e.target.value)} className="h-12 border-gray-300 contact-input-focus rounded-lg" />
      </div>
      {error && <p className="text-sm text-red-600">{error}</p>}
      <Button type="submit" disabled={loading} className="btn-shine w-full bg-[#6AB023] hover:bg-[#5a9a1e] text-white font-bold rounded-full h-12 text-base gap-2">
        {loading ? <span className="flex items-center gap-2"><svg className="animate-spin w-5 h-5" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" /></svg>Envoi en cours...</span> : <><Send className="w-5 h-5" />Recevoir mon devis gratuit</>}
      </Button>
      <p className="text-xs text-gray-400 text-center">Sans engagement · Vos données restent confidentielles</p>
    </form>
  );
}

function StickyMobileCTA() {
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      setVisible(window.scrollY > 600);
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  if (!visible) return null;
  return (
    <div className="fixed bottom-0 left-0 right-0 z-50 bg-white border-t border-gray-200 shadow-[0_-4px_12px_rgba(0,0,0,0.08)] p-3 md:hidden">
      <a href="#devis" className="block">
        <Button className="btn-shine w-full bg-[#6AB023] hover:bg-[#5a9a1e] text-white font-bold rounded-full h-12 gap-2 text-base">
          <Send className="w-5 h-5" />Recevoir mon devis gratuit
        </Button>
      </a>
    </div>
  );
}

export default function InstallationPACLotEtGaronne() {
  const [reviews, setReviews] = useState([]);
  const [rating, setRating] = useState(5);
  const [total, setTotal] = useState(0);

  useEffect(() => {
    axios.get(`${API}/google-reviews`).then(res => {
      setReviews(res.data.reviews);
      setRating(res.data.rating);
      setTotal(res.data.total);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              entry.target.classList.add("revealed");
              observer.unobserve(entry.target);
            }
          });
        },
        { threshold: 0.08, rootMargin: "0px 0px -40px 0px" }
      );
      const elements = document.querySelectorAll(".scroll-reveal, .scroll-reveal-left, .scroll-reveal-right, .scroll-reveal-scale");
      elements.forEach((el) => observer.observe(el));
      return () => observer.disconnect();
    }, 100);
    return () => clearTimeout(timer);
  }, [reviews]);

  useEffect(() => {
  document.title = "Installation Pompe à Chaleur Lot-et-Garonne (47) | Alter Energies";
  const meta = document.querySelector('meta[name="description"]');
  const content = "Installation de pompe à chaleur en Lot-et-Garonne par Alter Energies. Chauffagiste certifié RGE QualiPAC, 500+ installations, 18 communes couvertes. Devis gratuit, aides MaPrimeRénov' et CEE gérées.";
  if (meta) { meta.setAttribute("content", content); } else { const m = document.createElement("meta"); m.name = "description"; m.content = content; document.head.appendChild(m); }
}, []);

  return (
    <div className="min-h-screen bg-white">

      <StickyMobileCTA />

      {/* Hero */}
      <section className="pt-24 pb-16 bg-gradient-to-b from-green-50 to-white">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <div className="hero-badge hero-badge-shimmer inline-flex items-center gap-2 bg-green-100 text-[#6AB023] rounded-full px-4 py-1.5 text-sm font-medium mb-6">
                <MapPin className="w-4 h-4" />Lot-et-Garonne (47) · Tout le département
              </div>
              <h1 className="hero-title text-4xl md:text-5xl font-extrabold text-gray-900 leading-tight mb-6">
                Installation Pompe à Chaleur en <span className="text-[#6AB023]">Lot-et-Garonne</span>
              </h1>
              <p className="hero-subtitle text-lg text-gray-600 mb-8 leading-relaxed">
                François Delattre, votre chauffagiste certifié RGE QualiPAC, installe des pompes à chaleur dans tout le Lot-et-Garonne depuis 20 ans. Plus de 500 chantiers réalisés, 18 communes couvertes, déplacement et devis gratuits partout dans le 47. Remplacement chaudière fioul ou gaz, aides MaPrimeRénov' et CEE intégralement gérées.
              </p>
              <div className="hero-cta flex flex-col sm:flex-row gap-4 mb-8">
                <a href="tel:0628514915"><Button className="bg-[#6AB023] hover:bg-[#5a9a1e] text-white font-bold rounded-full px-8 h-12 gap-2 text-base"><Phone className="w-5 h-5" />06 28 51 49 15</Button></a>
                <a href="#devis"><Button variant="outline" className="border-[#6AB023] text-[#6AB023] hover:bg-green-50 font-bold rounded-full px-8 h-12 text-base">Devis gratuit →</Button></a>
              </div>
              <div className="hero-trust flex flex-wrap gap-4 text-sm text-gray-600">
                <span className="flex items-center gap-1.5"><Shield className="w-4 h-4 text-[#6AB023]" />Certifié RGE QualiPAC</span>
                <span className="flex items-center gap-1.5"><Award className="w-4 h-4 text-[#6AB023]" />500+ installations</span>
                <span className="flex items-center gap-1.5"><MapPin className="w-4 h-4 text-[#6AB023]" />18 communes couvertes</span>
              </div>
            </div>
            <div className="hero-image relative">
              <div className="bg-green-50 rounded-2xl p-2">
                <img src="https://customer-assets.emergentagent.com/job_chauffage-pac/artifacts/d8avbt7b_IMG_8167.jpeg" alt="Installation pompe à chaleur en Lot-et-Garonne - Alter Energies chauffagiste RGE 47" className="w-full rounded-xl object-cover" width={600} height={400} />
              </div>
              <div className="absolute -bottom-4 -left-4 bg-white rounded-xl shadow-lg p-4 flex items-center gap-3">
                <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center"><ThumbsUp className="w-5 h-5 text-[#6AB023]" /></div>
                <div>
                  <p className="font-bold text-gray-900 text-sm">20 ans d'expérience</p>
                  <p className="text-xs text-gray-500">dans tout le 47</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Chiffres clés */}
      <section className="py-12 bg-[#6AB023]">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center text-white">
            {[
              { value: "500+", label: "Installations réalisées" },
              { value: "20 ans", label: "D'expérience" },
              { value: "18", label: "Communes couvertes" },
              { value: "950€*", label: "Reste à charge moyen" },
            ].map((stat, idx) => (
              <div key={stat.label} className={`scroll-reveal-scale stagger-${idx + 1}`}>
                <p className="text-3xl md:text-4xl font-extrabold mb-1">{stat.value}</p>
                <p className="text-sm text-white/80">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Zone d'intervention — LE HUB */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="text-center mb-12 scroll-reveal">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">François intervient dans <span className="text-[#6AB023]">tout le Lot-et-Garonne</span></h2>
            <p className="text-gray-600 max-w-2xl mx-auto">Trouvez votre commune ci-dessous. Déplacement et diagnostic gratuits, où que vous soyez dans le 47.</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 max-w-5xl mx-auto">
            {COMMUNES.map((commune, idx) => (
          <a 
                key={commune.name}
                href={commune.url}
                className={`scroll-reveal-scale stagger-${(idx % 3) + 1} group flex items-center justify-between p-4 bg-white border border-gray-200 rounded-xl hover:border-[#6AB023] hover:shadow-md transition-all`}
              >
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 bg-green-50 rounded-full flex items-center justify-center group-hover:bg-[#6AB023] transition-colors">
                    <MapPin className="w-4 h-4 text-[#6AB023] group-hover:text-white transition-colors" />
                  </div>
                  <div>
                    <p className="font-semibold text-gray-900 text-sm group-hover:text-[#6AB023] transition-colors">{commune.name}</p>
                    <p className="text-xs text-gray-400">{commune.distance} · {commune.pop}</p>
                  </div>
                </div>
                <ArrowRight className="w-4 h-4 text-gray-300 group-hover:text-[#6AB023] transition-colors" />
              </a>
            ))}
          </div>
          <div className="text-center mt-8 scroll-reveal">
            <p className="text-sm text-gray-500 mb-4">Votre commune n'est pas listée ? François se déplace partout dans le 47.</p>
            <a href="#devis"><Button className="btn-shine bg-[#6AB023] hover:bg-[#5a9a1e] text-white font-bold rounded-full px-8 h-12 text-base gap-2"><Send className="w-5 h-5" />Demander un devis gratuit</Button></a>
          </div>
        </div>
      </section>

      {/* Parcours client */}
      <section className="py-16 bg-[#F9FAFB] border-b border-gray-100">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="text-center mb-12 scroll-reveal">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Comment ça marche, <span className="text-[#6AB023]">partout dans le 47</span></h2>
            <p className="text-gray-600 max-w-2xl mx-auto">Le même processus simple et transparent, quelle que soit votre commune en Lot-et-Garonne.</p>
          </div>
          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {[
              { step: "1", icon: Phone, title: "Contactez-nous", desc: "Un appel ou un formulaire. François vous rappelle sous 24h pour discuter de votre projet, où que vous soyez dans le 47." },
              { step: "2", icon: ClipboardCheck, title: "Visite gratuite à domicile", desc: "François vient chez vous, partout dans le département, pour analyser votre installation et remettre un devis complet, aides déduites." },
              { step: "3", icon: Wrench, title: "Installation en 1 à 2 jours", desc: "Pose certifiée RGE QualiPAC. François gère aussi vos dossiers MaPrimeRénov' et CEE intégralement et sans frais." },
            ].map((item, idx) => (
              <div key={item.step} className={`relative text-center scroll-reveal-scale stagger-${idx + 1}`}>
                <div className="w-14 h-14 bg-[#6AB023] rounded-full flex items-center justify-center mx-auto mb-5 text-white font-extrabold text-xl shadow-md">{item.step}</div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">{item.title}</h3>
                <p className="text-sm text-gray-600 leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Aides financières */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="text-center mb-12 scroll-reveal">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Aides maximales dans <span className="text-[#6AB023]">tout le 47</span></h2>
            <p className="text-gray-600 max-w-2xl mx-auto">Les mêmes aides record pour tous les habitants du Lot-et-Garonne. François vous en fait profiter au maximum.</p>
          </div>
          <div className="grid md:grid-cols-3 gap-6 mb-10">
            {[
              { title: "MaPrimeRénov'", amount: "jusqu'à 5 000€", desc: "Aide de l'État pour abandonner fioul et gaz" },
              { title: "CEE", amount: "jusqu'à 6 550€", desc: "Primes énergie au plus haut depuis mars 2025" },
              { title: "TVA réduite", amount: "5,5%", desc: "Taux préférentiel sur l'ensemble des travaux" },
            ].map((aide, idx) => (
              <Card key={aide.title} className={`scroll-reveal-scale stagger-${idx + 1} border border-gray-100 rounded-xl shadow-sm hover:shadow-md transition-shadow`}>
                <CardContent className="p-6 text-center">
                  <p className="text-2xl font-extrabold text-[#6AB023] mb-1">{aide.amount}</p>
                  <p className="font-bold text-gray-900 mb-2">{aide.title}</p>
                  <p className="text-xs text-gray-500 leading-relaxed">{aide.desc}</p>
                </CardContent>
              </Card>
            ))}
          </div>
          <div className="max-w-3xl mx-auto scroll-reveal">
            <Card className="border-2 border-[#6AB023]/20 rounded-xl bg-white shadow-sm">
              <CardContent className="p-6 md:p-8">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center"><Home className="w-5 h-5 text-[#6AB023]" /></div>
                  <h3 className="text-lg font-bold text-gray-900">Exemple : maison 100m² en Lot-et-Garonne</h3>
                </div>
                <div className="grid sm:grid-cols-2 gap-6">
                  <div className="space-y-3">
                    <div className="flex justify-between text-sm"><span className="text-gray-600">PAC Air/Eau complète</span><span className="font-semibold text-gray-900">12 500€</span></div>
                    <div className="flex justify-between text-sm"><span className="text-gray-600">MaPrimeRénov'</span><span className="font-semibold text-[#6AB023]">- 5 000€</span></div>
                    <div className="flex justify-between text-sm"><span className="text-gray-600">Prime CEE</span><span className="font-semibold text-[#6AB023]">- 6 550€</span></div>
                    <div className="border-t border-gray-200 pt-3 flex justify-between"><span className="font-bold text-gray-900">Vous déboursez</span><span className="font-extrabold text-[#6AB023] text-xl">950€*</span></div>
                  </div>
                  <div className="bg-green-50 rounded-lg p-4 flex flex-col justify-center">
                    <p className="text-sm text-gray-700 leading-relaxed"><span className="font-bold text-[#6AB023]">92% financés par les aides.</span> François gère l'intégralité de l'administratif, partout dans le 47.</p>
                    <p className="text-xs text-gray-400 mt-3">*Montant indicatif selon revenus du foyer.</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Services PAC */}
      <section className="py-16 bg-[#F9FAFB]">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="text-center mb-12 scroll-reveal">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Deux <span className="text-[#6AB023]">technologies PAC</span> pour le Lot-et-Garonne</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">François vous oriente vers la pompe à chaleur la plus adaptée à votre logement, quel que soit votre type de chauffage actuel.</p>
          </div>
          <div className="grid md:grid-cols-2 gap-8">
            {[
              { title: "PAC Air/Eau", desc: "La solution privilégiée dans le 47 pour les maisons équipées de radiateurs ou d'un plancher chauffant. Elle remplace votre chaudière fioul ou gaz sans toucher au réseau existant et produit aussi l'eau chaude sanitaire.", points: ["Se raccorde à vos radiateurs et plancher chauffant", "Production d'eau chaude sanitaire intégrée", "Économies de chauffage de 50 à 70%"] },
              { title: "PAC Air/Air", desc: "Chauffage et climatisation réversible en un seul appareil. Un atout majeur en Lot-et-Garonne, département aux étés chauds, pour un confort optimal toute l'année sans réseau de radiateurs.", points: ["Double fonction : chaud l'hiver, frais l'été", "Installation rapide en une journée", "Pilotage indépendant pièce par pièce"] },
            ].map((service, idx) => (
              <Card key={service.title} className={`scroll-reveal-scale stagger-${idx + 1} service-card border border-gray-100 rounded-xl shadow-sm`}>
                <CardContent className="p-8">
                  <h3 className="text-xl font-bold text-gray-900 mb-3">Installation {service.title} en Lot-et-Garonne</h3>
                  <p className="text-gray-600 text-sm leading-relaxed mb-4">{service.desc}</p>
                  <ul className="space-y-2">
                    {service.points.map((point) => (
                      <li key={point} className="flex items-start gap-2 text-sm text-gray-700"><span className="text-[#6AB023] font-bold mt-0.5">→</span>{point}</li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Avis Google */}
      {reviews.length > 0 && (
        <section className="py-16 bg-white">
          <div className="container mx-auto px-4 md:px-6 max-w-7xl">
            <div className="text-center mb-10 scroll-reveal">
              <div className="inline-flex items-center gap-2 mb-4"><GoogleLogo /><span className="text-sm font-medium text-gray-500">Avis Google vérifiés</span></div>
              <h2 className="text-3xl font-bold text-gray-900 mb-4">La référence PAC en <span className="text-[#6AB023]">Lot-et-Garonne</span></h2>
              <div className="flex items-center justify-center gap-3 mt-4">
                <span className="text-4xl font-bold text-gray-900">{rating.toFixed(1)}</span>
                <div><StarRating rating={Math.round(rating)} /><p className="text-xs text-gray-500 mt-1">{total} avis sur Google</p></div>
              </div>
            </div>
            <div className="grid md:grid-cols-3 gap-6 mb-8">
              {reviews.slice(0, 3).map((review, idx) => (
                <div key={review.name} className={`scroll-reveal-scale stagger-${idx + 1}`}><ReviewCard review={review} /></div>
              ))}
            </div>
            <div className="text-center scroll-reveal">
              <a href={GOOGLE_MAPS_URL} target="_blank" rel="noreferrer">
                <Button variant="outline" className="border-[#6AB023] text-[#6AB023] hover:bg-green-50 rounded-full px-8 h-11 font-semibold gap-2"><GoogleLogo />Voir nos {total > 0 ? total : ""} avis Google<ExternalLink className="w-4 h-4" /></Button>
              </a>
            </div>
          </div>
        </section>
      )}

      {/* Formulaire devis */}
      <section id="devis" className="py-16 bg-[#F9FAFB]">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="text-center mb-12 scroll-reveal">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Votre <span className="text-[#6AB023]">devis gratuit</span> en Lot-et-Garonne</h2>
            <p className="text-gray-600">Installation pompe à chaleur dans le 47 — Gratuit et sans engagement</p>
          </div>
          <div className="grid md:grid-cols-2 gap-12 max-w-5xl mx-auto">
            <Card className="scroll-reveal-left bg-white border border-gray-200 rounded-xl shadow-lg">
              <CardContent className="p-6 md:p-8"><ContactForm rating={rating} total={total} /></CardContent>
            </Card>
            <div className="flex flex-col gap-5 justify-center scroll-reveal-right">
              <a href="tel:0628514915" className="contact-info-card flex items-center gap-4 p-4 bg-white rounded-xl border border-gray-100 group">
                <div className="w-11 h-11 bg-green-50 rounded-full flex items-center justify-center group-hover:bg-[#6AB023] transition-colors"><Phone className="w-5 h-5 text-[#6AB023] group-hover:text-white transition-colors" /></div>
                <div><p className="font-bold text-gray-900">06 28 51 49 15</p><p className="text-xs text-gray-500">Appel gratuit · Du lundi au samedi</p></div>
              </a>
              <a href="mailto:frs.delattre@gmail.com" className="contact-info-card flex items-center gap-4 p-4 bg-white rounded-xl border border-gray-100 group">
                <div className="w-11 h-11 bg-green-50 rounded-full flex items-center justify-center group-hover:bg-[#6AB023] transition-colors"><Mail className="w-5 h-5 text-[#6AB023] group-hover:text-white transition-colors" /></div>
                <div><p className="font-bold text-gray-900">frs.delattre@gmail.com</p><p className="text-xs text-gray-500">Réponse sous 24h</p></div>
              </a>
              <div className="contact-info-card flex items-center gap-4 p-4 bg-white rounded-xl border border-gray-100">
                <div className="w-11 h-11 bg-green-50 rounded-full flex items-center justify-center"><MapPin className="w-5 h-5 text-[#6AB023]" /></div>
                <div><p className="font-bold text-gray-900">Basé à Pinel-Hauterive</p><p className="text-xs text-gray-500">Déplacement gratuit dans tout le 47</p></div>
              </div>

              <div className="bg-green-50/60 rounded-xl p-5 space-y-3 mt-2">
                <p className="text-sm font-semibold text-gray-900">Ce que François fait pour vous :</p>
                <div className="space-y-2">
                  {[
                    "Déplacement gratuit dans tout le Lot-et-Garonne",
                    "Diagnostic et devis détaillé avec aides déduites",
                    "Montage intégral des dossiers MaPrimeRénov' et CEE",
                    "Installation certifiée RGE QualiPAC",
                  ].map((item) => (
                    <p key={item} className="flex items-start gap-2 text-sm text-gray-700"><CheckCircle className="w-4 h-4 text-[#6AB023] flex-shrink-0 mt-0.5" />{item}</p>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4 md:px-6 max-w-4xl">
          <div className="text-center mb-12 scroll-reveal">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Questions fréquentes — <span className="text-[#6AB023]">PAC en Lot-et-Garonne</span></h2>
          </div>
          <div className="space-y-3 scroll-reveal">
            {FAQS.map((faq, index) => <FAQItem key={faq.question} faq={faq} defaultOpen={index === 0} />)}
          </div>
        </div>
      </section>

      <div className="h-16 md:hidden" />

      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "Service",
        "name": "Installation pompe à chaleur Lot-et-Garonne",
        "description": "Installation de pompe à chaleur air/eau et air/air dans tout le Lot-et-Garonne (47) par Alter Energies, chauffagiste certifié RGE QualiPAC. 500+ installations, 18 communes couvertes, devis gratuit, éligible MaPrimeRénov'.",
        "provider": {
          "@type": "LocalBusiness",
          "name": "Alter Energies - François Delattre",
          "telephone": "+33628514915",
          "email": "frs.delattre@gmail.com",
          "address": { "@type": "PostalAddress", "streetAddress": "1899 Côte de Pinel Garrigou", "addressLocality": "Pinel-Hauterive", "postalCode": "47380", "addressRegion": "Lot-et-Garonne", "addressCountry": "FR" },
          "geo": { "@type": "GeoCoordinates", "latitude": "44.3833", "longitude": "0.7667" },
          "url": "https://alterenergies-47.com",
          "priceRange": "€€",
          "aggregateRating": { "@type": "AggregateRating", "ratingValue": "5.0", "reviewCount": "21", "bestRating": "5" }
        },
        "areaServed": [
  { "@type": "AdministrativeArea", "name": "Lot-et-Garonne" },
  { "@type": "City", "name": "Villeneuve-sur-Lot" },
  { "@type": "City", "name": "Tonneins" },
  { "@type": "City", "name": "Aiguillon" },
  { "@type": "City", "name": "Clairac" },
  { "@type": "City", "name": "Fumel" },
  { "@type": "City", "name": "Penne-d'Agenais" },
  { "@type": "City", "name": "Sainte-Livrade-sur-Lot" },
  { "@type": "City", "name": "Monflanquin" },
  { "@type": "City", "name": "Miramont-de-Guyenne" },
  { "@type": "City", "name": "Casseneuil" },
  { "@type": "City", "name": "Bias" },
  { "@type": "City", "name": "Pujols" },
  { "@type": "City", "name": "Cancon" },
  { "@type": "City", "name": "Monsempron-Libos" },
  { "@type": "City", "name": "Castillonnès" },
  { "@type": "City", "name": "Villeréal" },
  { "@type": "City", "name": "Damazan" },
  { "@type": "City", "name": "Port-Sainte-Marie" }
],
        "serviceType": ["Installation pompe à chaleur", "Remplacement chaudière fioul", "Remplacement chaudière gaz", "Installation PAC air/eau", "Installation PAC air/air", "Chauffagiste RGE QualiPAC"]
      })}} />

      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "FAQPage",
        "mainEntity": FAQS.map(faq => ({
          "@type": "Question",
          "name": faq.question,
          "acceptedAnswer": { "@type": "Answer", "text": faq.answer }
        }))
      })}} />

    </div>
  );
}
